import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../services/authWrapper";
import EnterOtpModal from "./enterOtpModal";
import useTranslator from "../../hooks/useTranslator";
import { toast } from "../../helper/common";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Icon,
  Modal,
  Notice,
  PageHeader,
  SkorLogo,
} from "../../components/design";
import "./applicationProfiles.css";

type Profile = {
  name: string;
  email: string;
  is_lead: boolean;
  is_guarantor?: boolean;
  profile_exists: boolean;
  requires_login: boolean;
  onboarding_status: "COMPLETED" | "PENDING" | "OUTDATED";
  require_update?: boolean;
  require_update_note?: string;
};

const ApplicationProfiles = () => {
  const { _t } = useTranslator();
  const translatorRef = useRef(_t);
  translatorRef.current = _t;
  const [profiles, setProfiles] = useState<Profile[]>([]);
  console.log("profiles", profiles);
  const [loading, setLoading] = useState(true);
  const [workingEmail, setWorkingEmail] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationName, setVerificationName] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [showResend, setShowResend] = useState(true);
  const [showTimer, setShowTimer] = useState(60);
  const [submitted, setSubmitted] = useState(false);

  const serverBase = (process.env.REACT_APP_SERVER_URL || "").replace(
    /\/$/,
    ""
  );
  const apiBase = serverBase.endsWith("/api/v1")
    ? serverBase
    : `${serverBase}/api/v1`;
  const managerToken = localStorage.getItem("token") || "";

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `${apiBase}/tenants/application-profiles`,
        { token: managerToken }
      );
      setProfiles(data.data?.profiles || []);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          translatorRef.current("unableToContinue")
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, managerToken]);

  useEffect(() => {
    void loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (!showOtp || showResend) return;
    const timer = window.setInterval(() => {
      setShowTimer((seconds) => {
        if (seconds > 1) return seconds - 1;
        window.clearInterval(timer);
        setShowResend(true);
        return 60;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [showOtp, showResend]);

  const completedCount = useMemo(
    () =>
      profiles.filter(
        ({ onboarding_status }) => onboarding_status === "COMPLETED"
      ).length,
    [profiles]
  );

  const hasPendingUpdates = useMemo(
    () => profiles.some((profile) => Boolean(profile.require_update)),
    [profiles]
  );

  const launchOnboarding = useCallback(
    (token: string, profile?: Profile) => {
      sessionStorage.setItem("applicationProfileManagerToken", managerToken);
      sessionStorage.setItem("applicationProfiles", JSON.stringify(profiles));
      if (profile) {
        sessionStorage.setItem("activeApplicationProfileEmail", profile.email);
      }
      sessionStorage.removeItem("applicationProfileEditMode");
      localStorage.setItem("token", token);
      localStorage.setItem("application_profile_child", "true");
      const query = new URLSearchParams({
        token,
        usertype: "tenant",
        is_verified: "true",
        app_type: "skor-sdk",
        go_to_page: "/onboarding?welcome=true",
      });
      window.location.assign(`/redirect?${query.toString()}`);
    },
    [managerToken, profiles]
  );

  const launchEditOnboarding = useCallback(
    (token: string, profile?: Profile) => {
      sessionStorage.setItem("applicationProfileManagerToken", managerToken);
      sessionStorage.setItem("applicationProfiles", JSON.stringify(profiles));
      if (profile) {
        sessionStorage.setItem("activeApplicationProfileEmail", profile.email);
      }
      sessionStorage.setItem("applicationProfileEditMode", "true");
      localStorage.setItem("token", token);
      localStorage.setItem("application_profile_child", "true");
      const query = new URLSearchParams({
        token,
        usertype: "tenant",
        is_verified: "true",
        app_type: "skor-sdk",
        go_to_page: "/onboarding?welcome=true&editProfile=true",
      });
      window.location.assign(`/redirect?${query.toString()}`);
    },
    [managerToken, profiles]
  );

  const startProfile = async (profile: Profile, isEdit = false) => {
    setWorkingEmail(profile.email);
    try {
      const { data } = await axiosInstance.post(
        `${apiBase}/tenants/application-profiles/start`,
        {
          token: managerToken,
          tenant_email: profile.email,
          ...(isEdit ? { is_edit: true } : {}),
        }
      );
      if (data.data?.completed) {
        if (isEdit && data.data?.onboarding_token) {
          launchEditOnboarding(data.data.onboarding_token, profile);
          return;
        }
        if (isEdit) {
          toast.error(_t("unableToContinue"));
          return;
        }
        await loadProfiles();
        return;
      }
      if (data.data?.requires_verification) {
        setVerificationToken(data.data.verification_token);
        setVerificationEmail(profile.email);
        setVerificationName(profile.name);
        setOtp("");
        setShowOtp(false);
        return;
      }
      if (data.data?.onboarding_token) {
        if (isEdit) {
          launchEditOnboarding(data.data.onboarding_token, profile);
        } else {
          launchOnboarding(data.data.onboarding_token, profile);
        }
      }
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message || _t("unableToContinue")
      );
    } finally {
      setWorkingEmail("");
    }
  };

  const resendOtp = async () => {
    try {
      await axiosInstance.post(`${apiBase}/tenants/existing-profile/send-otp`, {
        token: verificationToken,
      });
      setShowTimer(60);
      setShowResend(false);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message || _t("failedToSendOtp")
      );
    }
  };

  const sendVerificationCode = async () => {
    if (!verificationToken || otpLoading) return;
    setOtpLoading(true);
    try {
      await axiosInstance.post(`${apiBase}/tenants/existing-profile/send-otp`, {
        token: verificationToken,
      });
      setOtp("");
      setShowTimer(60);
      setShowResend(false);
      setShowOtp(true);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message || _t("failedToSendOtp")
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (event?: any) => {
    event?.preventDefault?.();
    if (otp.length !== 6 || otpLoading) return;
    setOtpLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `${apiBase}/tenants/existing-profile/verify-otp`,
        { token: verificationToken, otp }
      );
      setVerificationToken("");
      if (data.data?.requires_onboarding && data.data?.onboarding_token) {
        launchOnboarding(
          data.data.onboarding_token,
          profiles.find(({ email }) => email === verificationEmail)
        );
        return;
      }
      if (data.data?.is_profile_outdated && data.data?.renewal_token) {
        const renewal = await axiosInstance.post(
          `${apiBase}/tenants/existing-profile/reinitiate-onboarding`,
          { renewal_token: data.data.renewal_token }
        );
        launchOnboarding(
          renewal.data.data.token,
          profiles.find(({ email }) => email === verificationEmail)
        );
        return;
      }
      await loadProfiles();
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          _t("OTPDoesnotMatchesPleasetryagain")
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const submitApplication = () => {
    setSubmitted(true);
    window.setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("application_profile_child");
      sessionStorage.removeItem("applicationProfileManagerToken");
      sessionStorage.removeItem("applicationProfiles");
      sessionStorage.removeItem("activeApplicationProfileEmail");
      window.parent.postMessage(
        {
          type: "TENANT_ONBOARDING_COMPLETED",
          data: { application_completed: true },
        },
        "*"
      );
    }, 1000);
  };

  if (submitted) {
    return (
      <main className="application-profiles-page">
        <Card className="application-profiles-card application-profiles-submitted">
          <Icon name="checkCircle" size={48} />
          <PageHeader title={_t("applicationSubmitted")} />
        </Card>
      </main>
    );
  }

  return (
    <main className="application-profiles-page">
      <Card className="application-profiles-card">
        <PageHeader
          title={_t("applicationProfiles")}
          description={
            <>
              {completedCount} {_t("of")} {profiles.length}{" "}
              {_t("profilesCompletedDescription")}
            </>
          }
        />
        <Notice
          title={_t("haveTheseHandy")}
          className="application-profiles-help"
        >
          <ul>
            <li>
              <Icon name="idCard" size={16} />
              {_t("idOrPassport")}
            </li>
            <li>
              <Icon name="briefcase" size={16} />
              {_t("proofOfWorkAndIncome")}
            </li>
          </ul>
          <small>{_t("neededForEachTenant")}</small>
        </Notice>
        <div className="application-profiles-list">
          {profiles.map((profile) => {
            const completed = profile.onboarding_status === "COMPLETED";
            const outdated = profile.onboarding_status === "OUTDATED";
            const requiresUpdate = Boolean(profile.require_update);
            const statusNote = profile.require_update_note || "";
            return (
              <div
                className={`application-profile-row${requiresUpdate ? " is-changes-requested" : ""}`}
                key={profile.email}
              >
                <Avatar name={profile.name || profile.email} size="md" />
                <span className="application-profile-meta">
                  <span className="application-profile-name">
                    <strong>
                      {profile.is_lead ? _t("you") : profile.name}
                    </strong>
                    {profile.is_guarantor && (
                      <span className="application-profile-role">
                        {_t("guarantor", "Guarantor")}
                      </span>
                    )}
                  </span>
                  <small className={requiresUpdate ? "is-update-note" : ""}>
                    {requiresUpdate
                      ? statusNote ||
                        _t("changesRequestedNote", "Changes requested")
                      : completed
                        ? _t("profileCompleted")
                        : profile.requires_login
                          ? _t("loginToLinkProfile")
                          : outdated
                            ? _t("profileOutdated")
                            : _t("notStarted")}
                  </small>
                </span>
                {requiresUpdate || completed ? (
                  <span className="application-profile-actions">
                    <Badge tone={requiresUpdate ? "warning" : "lime"}>
                      <Icon
                        name={requiresUpdate ? "warning" : "checkCircle"}
                        size={16}
                      />
                      {requiresUpdate
                        ? _t("changesRequested", "Changes requested")
                        : _t("completed")}
                    </Badge>
                    <Button
                      variant="secondary"
                      size="sm"
                      iconOnly
                      iconStart={<Icon name="edit" size={16} />}
                      loading={workingEmail === profile.email}
                      disabled={workingEmail === profile.email || loading}
                      onClick={() => {
                        if (requiresUpdate) {
                          // #NOTE: we are setting the step change in the backend
                          void startProfile(profile);
                        } else {
                          void startProfile(profile, true);
                        }
                      }}
                      aria-label={`${_t("edit", "Edit")} ${profile.name}`}
                    />
                  </span>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="application-profile-start"
                    loading={workingEmail === profile.email}
                    iconStart={
                      profile.requires_login ? (
                        <Icon name="shieldCheck" size={16} />
                      ) : undefined
                    }
                    iconEnd={<Icon name="chevronRight" size={16} />}
                    disabled={workingEmail === profile.email || loading}
                    onClick={() => void startProfile(profile)}
                  >
                    {profile.requires_login
                      ? _t("loginWithSkor")
                      : outdated
                        ? _t("profileIsOutdatedRedoOnboarding")
                        : _t("continueManually")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <Button
          size="lg"
          block
          className="application-profiles-submit"
          disabled={!profiles.length || completedCount !== profiles.length}
          onClick={submitApplication}
        >
          {_t("sendApplication")}
        </Button>
      </Card>
      {verificationToken && (
        <>
          {!showOtp && (
            <Modal
              open
              size="sm"
              align="start"
              dismissible={false}
              title={_t("tenantAlreadyHasSkorProfile")}
              icon={
                <SkorLogo className="application-profile-verification-logo" />
              }
              description={
                <>
                  {_t("sendVerificationCodeDescription")} {verificationEmail}
                </>
              }
              onClose={() => {
                setVerificationToken("");
                setVerificationEmail("");
                setVerificationName("");
              }}
              actionsLayout="stack"
              actions={
                <>
                  <Button
                    size="lg"
                    loading={otpLoading}
                    onClick={() => void sendVerificationCode()}
                  >
                    {otpLoading
                      ? _t("sendingCode")
                      : _t("sendVerificationCode")}
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => {
                      setVerificationToken("");
                      setVerificationEmail("");
                      setVerificationName("");
                    }}
                  >
                    {_t("cancel")}
                  </Button>
                </>
              }
            >
              <p>
                {_t("forTenant")} {verificationName}
              </p>
            </Modal>
          )}
          {showOtp && (
            <Modal
              open
              size="sm"
              align="start"
              title={_t("checkYourEmailForOtp")}
              description={
                <>
                  {_t("anOtpHasBeenSentToYourEmailPleaseCheck")}{" "}
                  <strong>{verificationEmail}</strong>
                </>
              }
              onClose={() => {
                setVerificationToken("");
                setVerificationEmail("");
                setVerificationName("");
                setShowOtp(false);
              }}
            >
              <EnterOtpModal
                inline
                hideHeader
                otp={otp}
                setOtp={setOtp}
                onClose={() => {
                  setVerificationToken("");
                  setVerificationEmail("");
                  setVerificationName("");
                  setShowOtp(false);
                }}
                showResend={showResend}
                showTimer={showTimer}
                sendOtpFun={resendOtp}
                verifyOTPHandler={verifyOtp}
                verifyOtpLoading={otpLoading}
                email={verificationEmail}
              />
            </Modal>
          )}
        </>
      )}
    </main>
  );
};

export default ApplicationProfiles;
